import { Camera, Info, Sparkle, TrendDown, TrendUp } from '@phosphor-icons/react';

import { TooltipContent, TooltipProvider, TooltipRoot, TooltipTrigger } from '../ui/Tooltip';
import { CardErrorState, ERROR_BOX_CLASS } from '../common/CardErrorState';
import { formatSelfieShortDate } from '../../lib/patrimonioComposition';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';

/**
 * `.pen` → `ds/card/networth-readout` (`x5FFC`).
 *
 * Cartão de superfície (`$surface-card` / `$border` / `$radius-ds-lg`,
 * `padding $space-5`, sem sombra), com `body` (`gap $space-2`):
 * - `head` (`gap $space-3`): `head-row` `space_between` com «O TEU PATRIMÓNIO»
 *   `$type-section` + info 14 à esquerda e o badge `ds/badge/highlight--sage`
 *   (`$sage-600`, `sparkle` 14 `$sage-100`, `$type-label` 600 `$sage-50`) à
 *   direita; a seguir o readout `$type-display-kpi` em `$fg`
 * - `delta` (`gap $space-2`): ícone 16 + valor `$type-body-sm` 600 em
 *   `$success-fg` (ou `$warning-fg` na quebra) + meta em `$fg-muted`
 * - `note`: `ds/message--warning` compacto, ligado só quando o valor desceu
 *
 * Densidade: a instância de 768 (coluna de 318px) reduz o meta a `$type-label`.
 * `.type-*` são classes CSS simples (não utilities Tailwind), por isso a
 * escolha é feita em JS a partir do `layout` já calculado na página.
 *
 * O master ganhou também um bloco `error` centrado.
 */
export function PatrimonioHeroCard({
  value,
  change,
  snapshotDate,
  growthStreak = 0,
  isFirstSelfie = false,
  layout = 'mobile',
  isError = false,
  onRetry,
  className,
}) {
  const hasChange = change !== null && change !== undefined;
  const isNegative = hasChange && change < 0;
  const dateLabel = formatSelfieShortDate(snapshotDate);

  const streakLabel =
    growthStreak === 1
      ? pt.patrimonio.streak.growingOne
      : pt.patrimonio.streak.growingMany.replace('{count}', String(growthStreak));

  const DeltaIcon = isFirstSelfie ? Camera : isNegative ? TrendDown : TrendUp;
  const deltaTone = isNegative ? 'text-warning-fg' : 'text-success-fg';
  const metaLabel = isFirstSelfie
    ? pt.patrimonio.hero.firstSelfie.replace('{date}', dateLabel || '')
    : dateLabel
      ? pt.patrimonio.hero.sinceLastSelfie.replace('{date}', dateLabel)
      : null;

  const cardClass = cn(
    'flex w-full min-w-0 flex-col rounded-ds-lg border border-border bg-card p-space-5',
    className,
  );

  if (isError) {
    return (
      <section className={cn(cardClass, ERROR_BOX_CLASS)}>
        <CardErrorState message={pt.common.netWorthLoadError} onRetry={onRetry} />
      </section>
    );
  }

  return (
    <section className={cardClass}>
      <div className="flex min-w-0 flex-col gap-space-2">
        {/* `.pen` › `head` */}
        <div className="flex min-w-0 flex-col gap-space-3">
          <div className="flex min-w-0 items-center justify-between gap-space-3">
            <div className="flex min-w-0 items-center gap-space-1">
              <p className="type-section text-muted-foreground">{pt.patrimonio.hero.label}</p>
              <TooltipProvider delayDuration={150}>
                <TooltipRoot>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="Mais informação"
                      className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-muted-foreground focus-visible:outline-none"
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{pt.patrimonio.hero.tooltip}</TooltipContent>
                </TooltipRoot>
              </TooltipProvider>
            </div>

            {growthStreak >= 2 ? (
              <span className="type-label inline-flex shrink-0 items-center gap-space-2 rounded-ds-lg bg-sage-600 px-space-3 py-space-1 text-sage-50">
                <Sparkle className="h-3.5 w-3.5 shrink-0 text-sage-100" aria-hidden />
                {streakLabel}
              </span>
            ) : null}
          </div>

          <p className="type-display-kpi text-foreground">{formatEUR(value)}</p>
        </div>

        {/* `.pen` › `delta` */}
        {metaLabel ? (
          <div className="flex min-w-0 flex-wrap items-center gap-space-2">
            <DeltaIcon className={cn('h-4 w-4 shrink-0', deltaTone)} aria-hidden />
            {!isFirstSelfie && hasChange ? (
              <span className={cn('type-body-sm font-display font-semibold tnum', deltaTone)}>
                {change > 0 ? '+' : ''}
                {formatEUR(change)}
              </span>
            ) : null}
            <span
              className={cn(
                'min-w-0 text-muted-foreground',
                layout === 'tablet' ? 'type-label-meta' : 'type-body-sm',
              )}
            >
              {metaLabel}
            </span>
          </div>
        ) : null}

        {/* `.pen` › `note` (ds/message--warning compacto) */}
        {isNegative ? (
          <p
            className="type-body-sm mt-space-1 flex items-start gap-space-2-5 rounded-ds-md border border-warning-500 bg-warning-tint px-space-3 py-space-2-5 font-medium text-warning-fg dark:bg-warning-tint/20"
            data-testid="patrimonio-decline-note"
          >
            <Info className="h-4 w-4 shrink-0" aria-hidden />
            {pt.patrimonio.hero.declineNote}
          </p>
        ) : null}
      </div>
    </section>
  );
}

export default PatrimonioHeroCard;
